<template>
  <Teleport to="body">
    <motion.div
      v-if="modelValue"
      class="workspace-access-denied-overlay"
      :initial="{ opacity: 0 }"
      :animate="{ opacity: 1 }"
      @click.self="close"
    >
      <motion.div
        class="workspace-access-denied-card"
        :initial="{ opacity: 0, scale: 0.92, y: 24 }"
        :animate="{ opacity: 1, scale: 1, y: 0 }"
        :transition="{ type: 'spring', stiffness: 320, damping: 28 }"
      >
        <div class="workspace-access-denied-icon-wrap">
          <svg class="workspace-access-denied-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
        </div>
        <h2 class="workspace-access-denied-title">{{ t('workspaceDenied.title') }}</h2>
        <p class="workspace-access-denied-desc" v-html="t('workspaceDenied.message', { area: displayArea })" />
        <p class="workspace-access-denied-hint">{{ t('workspaceDenied.hint') }}</p>
        <motion.button
          type="button"
          class="workspace-access-denied-cta"
          :whileHover="{ scale: 1.02 }"
          :whileTap="{ scale: 0.98 }"
          @click="close"
        >
          {{ t('workspaceDenied.dismiss') }}
        </motion.button>
      </motion.div>
    </motion.div>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { motion } from 'motion-v'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  area: { type: String, default: '' }
})

const emit = defineEmits(['update:modelValue'])
const { t } = useI18n()

const displayArea = computed(() => props.area || t('workspaceDenied.defaultArea'))

const close = () => emit('update:modelValue', false)
</script>

<style scoped>
.workspace-access-denied-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(8px);
}
.workspace-access-denied-card {
  width: min(440px, 100%);
  padding: 32px 28px 24px;
  border-radius: 20px;
  border: 1px solid color-mix(in srgb, #f59e0b 35%, var(--border-primary));
  background: linear-gradient(165deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 32px 64px rgba(15, 23, 42, 0.35);
  text-align: center;
}
.workspace-access-denied-icon-wrap {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, #f59e0b 18%, transparent);
  border: 1px solid color-mix(in srgb, #f59e0b 35%, var(--border-primary));
}
.workspace-access-denied-icon {
  width: 28px;
  height: 28px;
  color: #f59e0b;
}
.workspace-access-denied-title {
  margin: 0 0 10px;
  font-size: 1.35rem;
  font-weight: 800;
  color: var(--text-primary);
}
.workspace-access-denied-desc {
  margin: 0 0 8px;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text-secondary);
}
.workspace-access-denied-hint {
  margin: 0 0 20px;
  font-size: 13px;
  color: var(--text-secondary);
}
.workspace-access-denied-cta {
  width: 100%;
  border: none;
  border-radius: 12px;
  padding: 14px 18px;
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: var(--accent-primary);
}
</style>
